/**
 * Solapa Facturación · Servicios del residente por switches ON/OFF (v3.9.0).
 *
 * El usuario decide sólo dos cosas: si el residente tiene el servicio y, en los
 * adicionales, a quién se le cobra. El comprobante, el talonario y la letra los
 * deriva el sistema y se muestran como consecuencia.
 */
import { useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { MoneyInput } from './MoneyInput';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CLASS_LABEL = {
  prestacional: 'prestacional',
  adicional: 'adicional',
};

/** Precio especial con estado local: confirma al salir del campo. */
function PriceCell({ assignment, disabled, onCommit, testId }) {
  const [val, setVal] = useState(assignment.price_override != null ? String(assignment.price_override) : '');
  return (
    <MoneyInput
      className="h-8 text-xs" value={val} placeholder="Lista" disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const next = val === '' ? null : Number(val);
        const prev = assignment.price_override ?? null;
        if (next !== prev) onCommit(next);
      }}
      data-testid={testId}
    />
  );
}

/** Misma cascada que `services/billing_scheme.py::resolve_emission`. */
export function deriveEmission(service, assignment, resident, insurer) {
  const override = ['proforma', 'arca'].includes(assignment?.talonario) ? assignment.talonario : null;
  const tal = (t) => override || t;
  const cls = service.service_class === 'particular' ? 'prestacional' : (service.service_class || 'adicional');
  const receptor = insurer?.receptor === 'resident' ? 'resident' : 'insurer';
  const payer = (resident?.responsibles || []).find(r => r.is_payment_responsible);
  const canInsurer = !!insurer && receptor === 'insurer';
  const choice = assignment?.adicional_target;
  // Del lado residente: el residente o su responsable de pago
  const sideTarget = (choice === 'responsible' && payer) ? 'responsible'
    : (choice === 'resident' ? 'resident' : (payer && resident?.billing_target_type === 'responsible' ? 'responsible' : 'resident'));
  const who = sideTarget === 'responsible' ? `${payer.name}${payer.relationship ? ` (${payer.relationship})` : ''}` : 'el residente';
  const missingFiscal = sideTarget === 'responsible' && (!payer?.tax_id || !payer?.iva_condition);

  if (cls === 'prestacional') {
    if (!insurer) {
      return { doc: 'FC', blocked: true, canInsurer, payer, sideTarget,
        text: 'Impedimento: elegí el convenio del residente en la solapa Médico.' };
    }
    if (receptor === 'resident') {
      const t = tal(insurer.talonario || service.talonario || 'proforma');
      return { doc: 'FC', canInsurer, payer, sideTarget, missingFiscal, talonario: t,
        text: `Factura a ${who} · ${t.toUpperCase()} · convenio ${insurer.name}` };
    }
    const cons = (insurer.billing_modality === 'consolidada');
    const t = tal(insurer.talonario || 'arca');
    return { doc: 'FC', canInsurer, payer, sideTarget, forcedInsurer: true, talonario: t,
      text: `Factura a ${insurer.name} · ${t.toUpperCase()}`
        + (cons ? ' · agrupada con el resto de los residentes del convenio' : ' · una factura por residente') };
  }

  const toIns = (choice ? choice === 'insurer' : canInsurer) && canInsurer;
  const t = tal(toIns ? (insurer.talonario || 'arca') : (service.talonario || 'proforma'));
  const doc = service.emission_mode === 'factura' ? 'FC' : 'ND';
  return {
    doc, canInsurer, payer, sideTarget, talonario: t,
    missingFiscal: !toIns && missingFiscal,
    text: `${doc === 'ND' ? 'Nota de Débito' : 'Factura'} ${t.toUpperCase()} a ${toIns ? insurer.name : who}`
      + (doc === 'ND' ? ', asociada a su última factura' : ''),
  };
}

export function ResidentBillingServices({ residentId, resident, services, insurers, assignments, canEdit, onChange }) {
  const [busy, setBusy] = useState('');
  const insurer = useMemo(
    () => insurers.find(i => i.insurer_id === resident?.billing_target_insurer_id) || null,
    [insurers, resident?.billing_target_insurer_id],
  );
  const abonos = useMemo(() => services.filter(s => (s.billing_type || 'abono') === 'abono' && s.is_active !== false), [services]);
  const byService = useMemo(() => {
    const m = {};
    (assignments || []).filter(a => a.is_active).forEach(a => { m[a.service_id] = a; });
    return m;
  }, [assignments]);

  const toggle = async (svc, on) => {
    setBusy(svc.service_id);
    try {
      if (on) {
        await axios.post(`${BACKEND_URL}/api/residents/${residentId}/service-assignments`, {
          resident_id: residentId, service_id: svc.service_id, quantity: 1, discount_pct: 0,
          adicional_target: svc.service_class === 'adicional' ? ((insurer && insurer.receptor !== 'resident') ? 'insurer' : 'resident') : null,
          valid_from: new Date().toISOString().slice(0, 10),
        }, { withCredentials: true });
      } else {
        await axios.delete(`${BACKEND_URL}/api/service-assignments/${byService[svc.service_id].assignment_id}`, { withCredentials: true });
      }
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(''); }
  };

  const patch = async (assignment, body) => {
    setBusy(assignment.service_id);
    try {
      await axios.put(`${BACKEND_URL}/api/service-assignments/${assignment.assignment_id}`, body, { withCredentials: true });
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(''); }
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-3" data-testid="resident-services-switches">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">C · Servicios de este residente</h3>
          <span className="text-[11px] text-muted-foreground">Los encendidos entran cada mes en la corrida masiva</span>
        </div>
        {abonos.length === 0 && <p className="text-xs text-muted-foreground">Sin abonos en el catálogo.</p>}
        {abonos.map(svc => {
          const a = byService[svc.service_id];
          const on = !!a;
          const d = deriveEmission(svc, a, resident, insurer);
          const cls = svc.service_class === 'particular' ? 'prestacional' : (svc.service_class || 'adicional');
          const convenioIds = insurer?.service_ids || [];
          const otherConvenio = cls === 'prestacional' && insurer && convenioIds.length > 0
            && !convenioIds.includes(svc.service_id);
          return (
            <div key={svc.service_id}
                 className={`rounded-lg border p-3 transition-opacity ${on ? '' : 'opacity-60'}`}
                 data-testid={`svc-block-${svc.service_id}`}>
              <div className="flex items-start gap-3">
                <Switch checked={on} disabled={!canEdit || busy === svc.service_id || (otherConvenio && !on)}
                        onCheckedChange={(v) => toggle(svc, v)}
                        data-testid={`svc-switch-${svc.service_id}`} />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{svc.code ? `${svc.code} · ` : ''}{svc.name}</span>
                    <Badge variant="outline" className="text-[10px]">{CLASS_LABEL[svc.service_class] || 'particular'}</Badge>
                    <Badge className={`text-[10px] ${d.doc === 'ND' ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'}`}>
                      {d.doc === 'ND' ? 'NOTA DE DÉBITO' : 'FACTURA'}
                    </Badge>
                    {on && d.talonario && (
                      <Badge variant="outline" className={`text-[10px] ${d.talonario === 'arca' ? 'border-indigo-400 text-indigo-700' : ''}`}>
                        {d.talonario.toUpperCase()}
                      </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground">Lista {fmt(svc.current_price)}</span>
                  </div>

                  {otherConvenio && (
                    <div className="rounded-md border border-slate-300 bg-slate-50 dark:bg-slate-900/40 px-2 py-1.5 text-[11px] text-muted-foreground"
                         data-testid={`svc-other-convenio-${svc.service_id}`}>
                      No corresponde a este residente: su convenio es <b>{insurer.name}</b> y este concepto pertenece a otro convenio.
                    </div>
                  )}

                  {on && (
                    <>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground">Cantidad</label>
                          <Input className="h-8 text-xs" type="number" defaultValue={a.quantity || 1}
                                 onBlur={(e) => Number(e.target.value) !== (a.quantity || 1) && patch(a, { quantity: Number(e.target.value) || 1 })}
                                 disabled={!canEdit} data-testid={`svc-qty-${svc.service_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Precio especial</label>
                          <PriceCell assignment={a} disabled={!canEdit}
                                     onCommit={(v) => patch(a, { price_override: v })}
                                     testId={`svc-price-${svc.service_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Bonificación %</label>
                          <Input className="h-8 text-xs" type="number" defaultValue={a.discount_pct || 0}
                                 onBlur={(e) => Number(e.target.value) !== (a.discount_pct || 0) && patch(a, { discount_pct: Number(e.target.value) || 0 })}
                                 disabled={!canEdit} data-testid={`svc-disc-${svc.service_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Vigente hasta</label>
                          <Input className="h-8 text-xs" type="date" defaultValue={a.valid_to || ''}
                                 onBlur={(e) => patch(a, { valid_to: e.target.value || null })}
                                 disabled={!canEdit} data-testid={`svc-validto-${svc.service_id}`} />
                        </div>
                      </div>

                      <div className="flex items-center gap-4 flex-wrap text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Comprobante:</span>
                          <select value={a.talonario || ''} disabled={!canEdit}
                                  onChange={(e) => patch(a, { talonario: e.target.value || null })}
                                  className="h-8 min-w-[168px] rounded-md border px-2 text-xs"
                                  data-testid={`svc-talonario-${svc.service_id}`}>
                            <option value="">Según el convenio ({(d.talonario || 'proforma').toUpperCase()})</option>
                            <option value="proforma">PROFORMA · efectivo</option>
                            <option value="arca">ARCA · fiscal</option>
                          </select>
                        </div>
                        {d.forcedInsurer && (
                          <span className="text-muted-foreground" data-testid={`svc-fixed-target-${svc.service_id}`}>
                            Se factura a <b>{insurer.name}</b>{insurer.billing_modality === 'consolidada' ? ' · agrupada' : ' · una factura por residente'}
                          </span>
                        )}
                        {/* v3.30.2 · Adicionales SIEMPRE al convenio (regla absoluta de negocio). El destinatario se oculta para evitar confusiones. Si no hay convenio o el convenio deriva al residente, la lógica del backend re-rutea al residente automáticamente y muestra el warning A8. */}
                        {!d.forcedInsurer && cls === 'adicional' && (
                          <span className="text-muted-foreground" data-testid={`svc-adicional-fixed-${svc.service_id}`}>
                            {d.canInsurer ? (
                              <>Adicional · se factura a <b>{insurer?.name || 'la obra social'}</b> — impacta CC del residente</>
                            ) : (
                              <>Adicional · sin convenio elegible, se factura al residente</>
                            )}
                          </span>
                        )}
                        {!d.forcedInsurer && cls !== 'adicional' && (
                        <div className="flex items-center gap-2 flex-wrap" data-testid={`svc-target-${svc.service_id}`}>
                          <span className="text-muted-foreground">¿A quién se le cobra?</span>
                          {[
                            ['resident', 'Al residente', false],
                            ['responsible', d.payer ? `A ${d.payer.name}` : 'Al familiar responsable', !d.payer],
                          ].map(([v, lbl, disabled]) => {
                            const active = d.sideTarget === v;
                            return (
                              <button key={v} type="button" disabled={!canEdit || disabled}
                                      onClick={() => patch(a, { adicional_target: v })}
                                      className={`px-3 py-1 rounded-full border text-[11px] transition-colors ${active
                                        ? 'border-violet-400 bg-violet-50 text-violet-700 font-semibold dark:bg-violet-950/40'
                                        : 'border-border hover:bg-muted'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                      data-testid={`svc-target-${svc.service_id}-${v}`}>
                                {lbl}
                              </button>
                            );
                          })}
                        </div>
                        )}
                      </div>

                      {d.talonario === 'proforma' && ['responsable_inscripto', 'monotributo'].includes(
                        d.sideTarget === 'responsible' ? d.payer?.iva_condition : resident?.fiscal_data?.iva_condition) && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-200"
                             data-testid={`svc-proforma-inscripto-${svc.service_id}`}>
                          A10 · El receptor está inscripto en AFIP y este concepto sale en PROFORMA (documento interno).
                          Si necesita comprobante fiscal, pasalo a ARCA.
                        </div>
                      )}

                      {d.missingFiscal && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-200"
                             data-testid={`svc-missing-fiscal-${svc.service_id}`}>
                          Al familiar le faltan CUIT/CUIL o condición de IVA (solapa Responsables): no se le puede emitir en ARCA.
                        </div>
                      )}

                      {d.blocked && (
                        <div className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800 dark:bg-rose-950/30 dark:text-rose-200"
                             data-testid={`svc-derived-${svc.service_id}`}>
                          {d.text}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default ResidentBillingServices;
